import React, { useEffect, useState } from 'react';
import {
  adminGetBannedEmails,
  adminMarkEmailsRead,
  callableErrorMessage,
  confirmAction,
  copyText,
  DISABLE_EMAIL_REPLIES,
  ENABLE_EMAIL_REPLIES,
  enableUser,
  fillUserAddresses,
  replyEmail,
  userPhotoCountLabel,
  userPhotoList
} from '../../../adminApi';

function previewText(message) {
  const text = String(message || '')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) {
    return 'No message';
  }
  return text.length > 160 ? `${text.slice(0, 160)}…` : text;
}

function EmailsPanel(props) {
  const [appeals, setAppeals] = useState([]);
  const [users, setUsers] = useState({});
  const [selectedId, setSelectedId] = useState('');
  const [replyMode, setReplyMode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setBusy(true);
    setError('');
    setReplyMode('');
    adminGetBannedEmails()
      .then((result) => {
        const nextAppeals = (result && result.appeals) || [];
        const nextUsers = (result && result.users) || {};
        setAppeals(nextAppeals);
        setUsers(nextUsers);
        setSelectedId((current) => {
          if (current && nextAppeals.some((appeal) => appeal.userId === current)) {
            return current;
          }
          return nextAppeals[0] ? nextAppeals[0].userId : '';
        });
        if (props.onChanged) {
          props.onChanged(nextAppeals.length);
        }
        fillUserAddresses(nextUsers, setUsers, props.onUsersUpdated);
      })
      .catch((err) => setError(callableErrorMessage(err)))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = appeals.find((appeal) => appeal.userId === selectedId);
  const user = selected ? users[selected.userId] : null;
  const photo = userPhotoList(user)[0];
  const templates = replyMode === 'enable' ? ENABLE_EMAIL_REPLIES : replyMode === 'disable' ? DISABLE_EMAIL_REPLIES : [];

  const sendReply = (template, shouldEnable) => {
    if (!selected) {
      return;
    }
    const label = shouldEnable
      ? `Enable this account and send “${template.title}”?`
      : `Keep this account disabled and send “${template.title}”?`;
    if (!confirmAction(label)) {
      return;
    }
    const messageIds = (selected.emails || []).map((email) => email.messageId).filter(Boolean);
    setBusy(true);
    setError('');
    const work = shouldEnable
      ? enableUser(selected.userId).then(() => replyEmail(messageIds, template.body))
      : replyEmail(messageIds, template.body);
    work
      .then(() => load())
      .catch((err) => {
        setError(callableErrorMessage(err));
        setBusy(false);
      });
  };

  const markRead = (messageIds, label) => {
    const ids = (messageIds || []).filter(Boolean);
    if (ids.length === 0) {
      return;
    }
    if (!confirmAction(label)) {
      return;
    }
    setBusy(true);
    setError('');
    adminMarkEmailsRead(ids)
      .then(() => load())
      .catch((err) => {
        setError(callableErrorMessage(err));
        setBusy(false);
      });
  };

  return (
    <section>
      <div className="admin-section-head">
        <div>
          <h2>Banned emails</h2>
          <p className="admin-muted">{appeals.length} unread appeal{appeals.length === 1 ? '' : 's'}</p>
        </div>
        <button type="button" className="admin-btn" onClick={load} disabled={busy}>Refresh</button>
      </div>
      {error ? <p className="admin-error">{error}</p> : null}
      {appeals.length === 0 && !busy ? (
        <p className="admin-empty">No banned emails to review.</p>
      ) : (
        <div className="admin-split">
          <div className="admin-list">
            {appeals.map((appeal) => {
              const account = users[appeal.userId];
              const first = (appeal.emails || [])[0];
              return (
                <button
                  type="button"
                  key={appeal.userId}
                  className={appeal.userId === selectedId ? 'admin-list-item is-active' : 'admin-list-item'}
                  onClick={() => {
                    setSelectedId(appeal.userId);
                    setReplyMode('');
                  }}
                >
                  <strong>{account ? account.name : 'Unknown'}</strong>
                  <p className="admin-muted">{appeal.emails.length} email{appeal.emails.length === 1 ? '' : 's'}</p>
                  <p className="admin-muted">{previewText(first && first.message)}</p>
                </button>
              );
            })}
          </div>
          {selected ? (
            <section className="admin-detail">
              <div className="admin-user">
                {photo ? <img src={photo.url} alt="" /> : <div className="admin-card__empty" style={{ width: 72, height: 72 }}>No photo</div>}
                <div>
                  <strong>
                    {user ? `${user.name || 'Unknown'}${user.age != null ? ` | ${user.age}` : ''}` : selected.userId}
                  </strong>
                  <p className="admin-muted">{user && user.active === 'false' ? 'Disabled' : 'Active'}</p>
                  <p className="admin-muted">{user ? userPhotoCountLabel(user) : ''}</p>
                </div>
              </div>
              {(selected.emails || []).map((email, index) => (
                <div key={email.messageId || index} className="admin-email">
                  <div className="admin-section-head">
                    <p className="admin-muted">Appeal {index + 1}{email.bannedCode ? ` · ${email.bannedCode}` : ''}</p>
                    {email.messageId ? (
                      <button
                        type="button"
                        className="admin-btn"
                        disabled={busy}
                        onClick={() => markRead([email.messageId], 'Mark this email as read? It will leave the queue.')}
                      >
                        Mark as read
                      </button>
                    ) : null}
                  </div>
                  <pre>{email.message || 'No message'}</pre>
                </div>
              ))}
              <div className="admin-actions">
                <button type="button" className="admin-btn admin-btn--primary" disabled={busy} onClick={() => setReplyMode('enable')}>
                  Enable account
                </button>
                <button type="button" className="admin-btn admin-btn--danger" disabled={busy} onClick={() => setReplyMode('disable')}>
                  Keep disabled
                </button>
                <button
                  type="button"
                  className="admin-btn"
                  disabled={busy}
                  onClick={() => markRead(
                    (selected.emails || []).map((email) => email.messageId),
                    `Mark all ${selected.emails.length} email(s) as read? They will leave the queue.`
                  )}
                >
                  Mark all as read
                </button>
                {user ? (
                  <button type="button" className="admin-btn" onClick={() => props.onPreview(user)}>
                    Preview
                  </button>
                ) : null}
                <button type="button" className="admin-btn" onClick={() => copyText(selected.userId)}>UserID</button>
              </div>
              {templates.length > 0 ? (
                <div>
                  <p className="admin-muted">
                    {replyMode === 'enable' ? 'Choose an unlock reply' : 'Choose a keep-disabled reply'}
                  </p>
                  <div className="admin-actions">
                    {templates.map((template) => (
                      <button
                        type="button"
                        key={template.id}
                        className="admin-btn"
                        disabled={busy}
                        onClick={() => sendReply(template, replyMode === 'enable')}
                      >
                        {template.title}
                      </button>
                    ))}
                    <button type="button" className="admin-btn" onClick={() => setReplyMode('')}>Cancel</button>
                  </div>
                </div>
              ) : null}
            </section>
          ) : (
            <p className="admin-empty">Pick an appeal to review it.</p>
          )}
        </div>
      )}
      {busy ? <p className="admin-muted">Working…</p> : null}
    </section>
  );
}

export default EmailsPanel;
